import React from "react";
import "./SmartEditFlow.css";

/**
 * SmartEditFlow — orchestrates layer-scoped edit passes.
 *
 * This component is NOT a separate page. It injects into the existing chat flow
 * by providing helper functions that App.js calls before/during the edit cycle.
 *
 * The flow:
 * 1. User sends an edit message
 * 2. App.js calls decomposeEdit() to classify concerns
 * 3. If multi-layer: shows FixPlanCard in chat, waits for approval
 * 4. Executes passes sequentially via streamChat() with layer param
 * 5. Merges results into the existing pending files
 */

/**
 * Determine if a NEW app prompt is complex enough to benefit from plan-first flow.
 * Checks for multiple data sources, complex layouts, or multi-section dashboards.
 *
 * @param {string} message - The user's creation prompt
 * @returns {boolean} True if plan-first generation is recommended
 */
export function isComplexCreation(message) {
  if (!message || message.length < 50) return false;

  const lower = message.toLowerCase();

  // Count data source indicators (need 2+ for "complex")
  const dataSourcePatterns = [
    /\bweather\b/, /\bnews\b/, /\bstock/, /\btransit/, /\bsocial\b/,
    /\bkalshi\b/, /\bpolymarket\b/, /\brodds\b/, /\bbetting\b/,
    /\bapi\b/gi, /\bfeed\b/, /\bdata source/, /\breal.?time/,
    /\bcountdown\b/, /\btimer\b/, /\bclock\b/,
    /\bscores?\b/, /\bschedule\b/, /\bupcoming games/,
    /\bkpi\b/, /\bmetric/, /\bleaderboard/,
  ];

  // Count layout complexity indicators
  const layoutPatterns = [
    /\b(top|bottom|left|right)\s+(half|side|section|part)/,
    /\bdivided\b/, /\bsplit\b/, /\b(2|two|three|3|four|4)\s*(column|row|section|panel|part)/,
    /\bgrid\b/, /\bmultiple\s+section/, /\bsidebar\b/,
  ];

  const dataSourceCount = dataSourcePatterns.filter((r) => r.test(lower)).length;
  const hasComplexLayout = layoutPatterns.some((r) => r.test(lower));
  const sectionCount = (lower.match(/\b(section|panel|part|area|side|half|zone)\b/g) || []).length;

  // Complex if: 2+ data sources, or complex layout + data, or many sections described
  return dataSourceCount >= 2 || (hasComplexLayout && dataSourceCount >= 1) || sectionCount >= 3;
}

/**
 * Determine if an edit message likely involves multiple layers.
 * Quick heuristic — no API call needed for simple cases.
 *
 * @param {string} message - The user's edit message
 * @returns {boolean} True if decomposition is recommended
 */
export function shouldDecompose(message) {
  if (!message || message.length < 20) return false;

  const lower = message.toLowerCase();

  // Count distinct concern indicators
  const dataIndicators = [
    /\bapi\b/, /\bdata\b/, /\bfetch/, /\bloading\b/, /\bendpoint/,
    /\bincorrect data\b/, /\bwrong (data|value|number)/, /\bnot (loading|showing|fetching)/,
    /\bbroken\b.*\bdata/, /\bdata\b.*\bbroken/,
  ];
  const designIndicators = [
    /\blayout\b/, /\bcolor/, /\bfont/, /\bstyl/, /\bdesign/,
    /\bcolumn/, /\brow/, /\bgrid/, /\bsize/, /\bbigger/, /\bsmaller/,
    /\bspacing/, /\bpadding/, /\bmargin/, /\balign/, /\bposition/,
    /\bimage.*wrong/, /\bwrong.*image/, /\bflag\b/, /\blogo\b/,
    /\banimation/, /\btransition/,
  ];

  // Detect NEGATIVE design references — user saying "DON'T change the design"
  // These override design indicators and force data-only routing
  const preserveDesignPatterns = [
    /don'?t\s+(want\s+to\s+)?(change|touch|modify|update|alter|redesign|redo)\s+(the\s+)?(design|layout|style|look|ui|visual)/i,
    /keep\s+(the\s+)?(design|layout|style|look|current\s+design|existing\s+design)/i,
    /(design|layout|style|look)\s+(looks?|is)\s+(great|good|perfect|fine|amazing|nice)/i,
    /don'?t\s+(want|need)\s+(a\s+)?(new|different)\s+(design|layout)/i,
    /leave\s+(the\s+)?(design|layout|style)\s+(as\s+is|alone|unchanged)/i,
    /only\s+(fix|change|update|modify)\s+(the\s+)?data/i,
  ];
  const userWantsToPreserveDesign = preserveDesignPatterns.some((r) => r.test(lower));

  const hasDataConcern = dataIndicators.some((r) => r.test(lower));
  const hasDesignConcern = userWantsToPreserveDesign ? false : designIndicators.some((r) => r.test(lower));

  // Decompose ONLY when the message genuinely touches BOTH layers. The old
  // code also returned true whenever clauseCount >= 3, which caused
  // single-layer design requests with a few commas/"ands" (e.g. "portrait
  // is fine, don't change it — the landscape is broken with overlapping
  // and off-screen elements") to get decomposed, waste a data-pass turn
  // via Haiku misclassification, and confuse the user with a "Forge Data"
  // response to their visual change request. If a request has multiple
  // concerns within ONE layer, the layer-scoped pass handles them fine;
  // decomposition is only valuable when the passes would route differently.
  return hasDataConcern && hasDesignConcern;
}

/**
 * Detect if the user explicitly wants to preserve the design.
 * When true, ALL edits should be routed to the data layer only.
 *
 * @param {string} message - The user's message
 * @returns {boolean}
 */
export function wantsToPreserveDesign(message) {
  if (!message) return false;
  const lower = message.toLowerCase();
  const patterns = [
    /don'?t\s+(want\s+to\s+)?(change|touch|modify|update|alter|redesign|redo)\s+(the\s+)?(design|layout|style|look|ui|visual)/i,
    /keep\s+(the\s+)?(design|layout|style|look|current\s+design|existing\s+design)/i,
    /(design|layout|style|look)\s+(looks?|is)\s+(great|good|perfect|fine|amazing|nice)/i,
    /don'?t\s+(want|need)\s+(a\s+)?(new|different)\s+(design|layout)/i,
    /leave\s+(the\s+)?(design|layout|style)\s+(as\s+is|alone|unchanged)/i,
    /only\s+(fix|change|update|modify)\s+(the\s+)?data/i,
  ];
  return patterns.some((r) => r.test(lower));
}

/**
 * Build the composite message for a specific pass.
 * Extracts only the concerns relevant to that layer.
 *
 * @param {Object} pass - { layer, files, concerns }
 * @param {string} originalMessage - The full user message
 * @returns {string} A focused message for the layer-specific Claude call
 */
export function buildPassMessage(pass, originalMessage) {
  if (pass.concerns.length === 0) return originalMessage;

  const descriptions = pass.concerns.map((c) => c.description).join("\n- ");
  return `The user requested these changes (${pass.layer} layer only):\n- ${descriptions}\n\nOriginal message: "${originalMessage}"`;
}

/**
 * FixPlanCard — renders inline in the chat to show the decomposition plan.
 */
export function FixPlanCard({ passes, onApprove, onSkip, executing, currentPass }) {
  if (!passes || passes.length === 0) return null;

  return (
    <div className="fix-plan-card">
      <div className="fix-plan-header">
        <span className="fix-plan-icon">&#9881;</span>
        <span>Smart Edit Plan</span>
      </div>
      <div className="fix-plan-body">
        <p className="fix-plan-desc">
          I'll fix this in {passes.length} {passes.length === 1 ? "pass" : "passes"} to avoid breaking anything:
        </p>
        <ol className="fix-plan-passes">
          {passes.map((pass, i) => (
            <li key={i} className={`fix-plan-pass ${executing && currentPass === i ? "active" : ""} ${executing && currentPass > i ? "done" : ""}`}>
              <span className="pass-layer-badge" data-layer={pass.layer}>
                {pass.layer === "data" ? "Data" : pass.layer === "design" ? "Design" : "Full"}
              </span>
              <span className="pass-concerns">
                {pass.concerns.map((c) => c.description).join(", ")}
              </span>
              {executing && currentPass === i && (
                <span className="pass-spinner" />
              )}
              {executing && currentPass > i && (
                <span className="pass-check">&#10003;</span>
              )}
            </li>
          ))}
        </ol>
      </div>
      {!executing && (
        <div className="fix-plan-actions">
          <button className="fix-plan-btn primary" onClick={onApprove}>
            Run plan — {passes.length} focused {passes.length === 1 ? "pass" : "passes"}
          </button>
          <button className="fix-plan-btn secondary" onClick={onSkip}>
            Skip plan — one big edit
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * BuildPlanCard — renders inline in chat for complex app creation plans.
 */
export function BuildPlanCard({ plan, onApprove, onChange }) {
  if (!plan) return null;

  return (
    <div className="build-plan-card">
      <div className="build-plan-header">
        <span className="build-plan-icon">&#128736;</span>
        <span>Build Plan</span>
      </div>
      <div className="build-plan-body">
        {plan.dataSources && plan.dataSources.length > 0 && (
          <div className="plan-section">
            <h4>Data Sources</h4>
            <ul>
              {plan.dataSources.map((ds, i) => (
                <li key={i}>
                  <strong>{ds.name}</strong> — {ds.description}
                  {/* API key requirements are determined during the research step, not here */}
                </li>
              ))}
            </ul>
          </div>
        )}
        {plan.sections && plan.sections.length > 0 && (
          <div className="plan-section">
            <h4>Dashboard Sections</h4>
            <ul>
              {plan.sections.map((s, i) => (
                <li key={i}>
                  <strong>{s.name}</strong> — {s.description}
                </li>
              ))}
            </ul>
          </div>
        )}
        {plan.suggestedLayout && (
          <div className="plan-section">
            <h4>Layout</h4>
            <p className="layout-badge">{plan.suggestedLayout}</p>
          </div>
        )}
      </div>
      <div className="build-plan-actions">
        <button className="build-plan-btn primary" onClick={onApprove}>
          Looks good, build it
        </button>
        <button className="build-plan-btn secondary" onClick={onChange}>
          I want to change something
        </button>
      </div>
    </div>
  );
}
